import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Dashboard from './components/Dashboard';
import Landing from './components/Landing';
import ErrorBoundary from './components/ErrorBoundary';
import AuthGate from './components/AuthGate';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary:    { main: '#c85448', light: '#d97068', dark: '#a83d34', contrastText: '#fff' },
    secondary:  { main: '#7c5cbf', light: '#9b7dd4', dark: '#5e3ea8', contrastText: '#fff' },
    error:      { main: '#f87171' },
    warning:    { main: '#fb923c' },
    success:    { main: '#4ade80' },
    background: { default: '#0c0a18', paper: '#13101f' },
    text:       { primary: '#e8e4f8', secondary: 'rgba(232,228,248,0.48)' },
    divider:    'rgba(255,255,255,0.07)',
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
    h6:         { fontWeight: 600 },
    subtitle2:  { fontWeight: 600 },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Subtle depth behind the whole app: a quiet radial wash, not flat black
          background: 'radial-gradient(1200px 600px at 12% -8%, rgba(124,92,191,0.10) 0%, rgba(12,10,24,0) 55%), radial-gradient(1000px 700px at 100% 0%, rgba(200,84,72,0.07) 0%, rgba(12,10,24,0) 50%), #0c0a18',
          backgroundAttachment: 'fixed',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '*::-webkit-scrollbar': { width: 7, height: 7 },
        '*::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.10)', borderRadius: 4 },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
        // Respect OS-level reduced-motion: freeze every pulse/spin/transition
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          // Keyboard-visible focus ring (invisible for mouse clicks)
          '&.Mui-focusVisible': {
            outline: '2px solid rgba(167,139,250,0.7)',
            outlineOffset: 2,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', fontWeight: 600, borderRadius: 8,
          boxShadow: 'none', '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          backgroundColor: '#c85448', color: '#fff',
          '&:hover': { backgroundColor: '#b8473c' },
          '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.25)' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        // A whisper of top-light instead of flat fill: gives panels depth
        // without shadows. Menus/dialogs override back to flat below.
        root: {
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 110px)',
          boxShadow: 'none', border: '1px solid rgba(255,255,255,0.07)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 110px)',
          boxShadow: 'none', border: '1px solid rgba(255,255,255,0.07)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': { backgroundColor: '#16132a' },
          '& .MuiTableCell-stickyHeader': { backgroundColor: '#16132a' },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: 'rgba(255,255,255,0.03) !important' } },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: 'rgba(255,255,255,0.06)',
          fontVariantNumeric: 'tabular-nums',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: 'rgba(200,84,72,0.15)', borderRadius: 4, height: 4 },
        bar:  { backgroundColor: '#c85448', borderRadius: 4 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#c85448' },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { '&.Mui-focused': { color: '#c85448' } } },
    },
    MuiSelect: {
      styleOverrides: { icon: { color: 'rgba(255,255,255,0.35)' } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600 } },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: 'rgba(255,255,255,0.07)' } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none', backgroundColor: '#1c1830',
          border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
          '&.Mui-selected': { backgroundColor: 'rgba(200,84,72,0.15)' },
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: { root: { '& .MuiAlert-root': { backgroundImage: 'none' } } },
    },
  },
});

function App() {
  // Serves two routes under the same React build + same auth context:
  //   /                      -> CertShield picker (landing)
  //   /domainprotection/...  -> DomainProtection dashboard
  // Both share the AuthGate, so the user signs in once via Keycloak and sees
  // whichever route they landed on.
  const pathname = window.location.pathname;
  const onDashboard = pathname.startsWith('/domainprotection');

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <AuthGate>
          {onDashboard ? <Dashboard /> : <Landing />}
        </AuthGate>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
